import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { canWrite, useAuth } from '../auth';
import { useSuppliers } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, Select, Spinner, number, unitMoney } from '../ui';

interface Vendor {
  supplierId: number;
  supplierName: string;
  preferred: boolean;
  vendorCode: string | null;
  leadTimeDays: number | null;
  orders: number;
  pieces: number;
  lastPrice: string | null;
  avgPrice: string | null;
  currency: string | null;
  lastOn: string | null;
}

/** Who this item can be bought from: what past orders say (price, how often, when), plus a preferred supplier, their code and lead time. */
export function VendorsTab({ itemId }: { itemId: number }) {
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const suppliers = useSuppliers();
  const q = useQuery({ queryKey: ['item-vendors', itemId], queryFn: () => api<{ vendors: Vendor[] }>(`/api/v1/items/${itemId}/vendors`).then((r) => r.vendors) });
  const [add, setAdd] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['item-vendors', itemId] });
  const save = useMutation({
    mutationFn: (v: { supplierId: number; vendorCode?: string | null; preferred?: boolean; leadTimeDays?: number | null }) =>
      api(`/api/v1/items/${itemId}/vendors/${v.supplierId}`, { method: 'PUT', body: { vendorCode: v.vendorCode ?? null, preferred: !!v.preferred, leadTimeDays: v.leadTimeDays ?? null } }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (supplierId: number) => api(`/api/v1/items/${itemId}/vendors/${supplierId}`, { method: 'DELETE' }), onSuccess: refresh });
  if (q.isPending) return <Spinner />;
  return (
    <Card title="Suppliers">
      <p className="mb-3 text-sm text-slate-600">What you have paid each supplier for this item, from your past orders. Mark one preferred: the buy list orders from the preferred supplier unless a reorder level names another.</p>
      <ErrorMessage error={q.error ?? save.error} />
      {q.data && q.data.length === 0 && <p className="text-sm text-slate-500">No supplier yet. Buy it on a purchase order with a seller, or add one below.</p>}
      {q.data && q.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="data w-full min-w-[44rem] text-sm">
            <thead>
              <tr>
                <th>Supplier</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Pieces</th>
                <th className="text-right">Last price</th>
                <th className="text-right">Average</th>
                <th>Last bought</th>
                <th>Their code</th>
                <th className="text-right">Lead time (days)</th>
                <th>Preferred</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((v) => (
                <tr key={v.supplierId}>
                  <td>
                    <Link to={`/suppliers/${v.supplierId}`} className="font-medium hover:underline">
                      {v.supplierName}
                    </Link>
                  </td>
                  <td className="text-right tabular-nums">{number(v.orders)}</td>
                  <td className="text-right tabular-nums">{number(v.pieces)}</td>
                  <td className="text-right tabular-nums">{v.lastPrice ? unitMoney(v.lastPrice, v.currency ?? 'GBP') : '—'}</td>
                  <td className="text-right tabular-nums">{v.avgPrice ? unitMoney(v.avgPrice, v.currency ?? 'GBP') : '—'}</td>
                  <td>{v.lastOn ?? '—'}</td>
                  <td>
                    {writable ? <Input aria-label={`Code for ${v.supplierName}`} className="w-28" defaultValue={v.vendorCode ?? ''} onBlur={(e) => e.target.value !== (v.vendorCode ?? '') && save.mutate({ ...v, vendorCode: e.target.value || null })} /> : (v.vendorCode ?? '—')}
                  </td>
                  <td className="text-right">
                    {writable ? <Input aria-label={`Lead time for ${v.supplierName}`} inputMode="numeric" className="w-16 text-right" defaultValue={v.leadTimeDays ?? ''} onBlur={(e) => (e.target.value === '' ? null : Number(e.target.value)) !== v.leadTimeDays && save.mutate({ ...v, leadTimeDays: e.target.value === '' ? null : Number(e.target.value) })} /> : (v.leadTimeDays ?? '—')}
                  </td>
                  <td>
                    {v.preferred ? (
                      <Badge tone="green">Preferred</Badge>
                    ) : (
                      writable && (
                        <Button variant="ghost" small onClick={() => save.mutate({ ...v, preferred: true })}>
                          Make preferred
                        </Button>
                      )
                    )}
                  </td>
                  <td className="text-right">
                    {writable && v.orders === 0 && (
                      <Button variant="ghost" small onClick={() => remove.mutate(v.supplierId)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {writable && (
        <div className="mt-3 flex items-end gap-2">
          <Field label="Add a supplier for this item">
            <Select value={add} onChange={(e) => setAdd(e.target.value)}>
              <option value="">Choose…</option>
              {(suppliers.data ?? []).filter((s) => !q.data?.some((v) => v.supplierId === s.id)).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button variant="secondary" disabled={!add} onClick={() => { save.mutate({ supplierId: Number(add) }); setAdd(''); }}>
            Add
          </Button>
        </div>
      )}
    </Card>
  );
}
